/**
 * useServerTable
 *
 * Bundles the row-type-agnostic scaffolding every server-driven
 * `@onengine/component-library` Table needs: the filter shim
 * (`useTableFilterOptions`), URL/preference persistence (`useTablePersistence`),
 * the derived `queryOptions` for the list query, and the repeated `<Table>` prop
 * block (`controlProps`). Callers keep only what is genuinely per-table — the
 * column definitions, the data hook, `loading`, `totalCount`, `onRowClick` and
 * `emptyText`.
 *
 * `controlProps` is deliberately free of the row generic, so it spreads into
 * `<Table<TRow> {...controlProps} />` for any row type without fighting the
 * generics.
 */

import { useMemo } from "react";
import { useTableColumnPrefs } from "./useTableColumnPrefs";
import { resetColumnsAction } from "@/components/table/resetColumnsAction";
import { useNavigate } from "@tanstack/react-router";
import type {
  FilterColumnOptions,
  TableCardAction,
  TableColumnPref,
} from "@onengine/component-library";
import {
  useTableFilterOptions,
  type TableFilterColumnConfig,
  type UseTableFilterOptionsOptions,
  type UseTableFilterOptionsResult,
} from "@/hooks/table/useTableFilterOptions";
import {
  parseFilterByToColumns,
  useTablePersistence,
  type TableSortState,
} from "@/hooks/table/useTablePersistence";
import { TABLE_PAGE_SIZE_OPTIONS } from "@/constants/tablePagination";
import type { TableUrlSearchParams } from "@/hooks/useTableUrlPersistence";
import type { FilterEntry } from "@/utils/buildFilterByParam";

export type ServerTableQueryOptions = {
  page: number;
  pageSize: number;
  search: string | undefined;
  sortBy: string | undefined;
  sortOrder: "asc" | "desc" | undefined;
  filters: FilterEntry[];
};

/**
 * The Table component groups whatever `data` it's handed, entirely
 * client-side -- `onGroup` only fires as a notification, never as a request
 * to the server for grouped data. So while a group-by column is active and
 * `enableServerSideGrouping` is on, the query below asks for every matching
 * row (up to this ceiling) in one page instead of the usual page size, or
 * grouping would silently only cover whatever page happened to be loaded.
 * Matches the raised limit caps on the only two list endpoints that opt in
 * (ListFormsQuerySchema, ProposalAllListQuerySchema) in onengine-backend. Do
 * not set `enableServerSideGrouping` on a grid whose endpoint still caps
 * `limit` at 100 -- every Group By click would 400.
 */
export const GROUPED_FETCH_LIMIT = 1000;

const noop = () => {};

export type ServerTableControlProps = {
  tableName: string;
  serverSide: true;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  sorting: TableSortState;
  onSort: (columnId: string | null, direction: "asc" | "desc") => void;
  onGroup: (columnId: string | null, direction: "asc" | "desc") => void;
  filters: Record<string, string[]>;
  onFilter: (filters: Record<string, string[]>) => void;
  onSearch: (value: string) => void;
  filterOptions: Record<string, FilterColumnOptions>;
  onFetchFilterOptions: UseTableFilterOptionsResult["onFetchFilterOptions"];
  onSearchFilterOptions: UseTableFilterOptionsResult["onSearchFilterOptions"];
  onLoadMoreFilterOptions: UseTableFilterOptionsResult["onLoadMoreFilterOptions"];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  cellClickIgnoreColumns: string[];
  /** Drag a header's right edge to resize (Jesus, 2026-10-27: on for every list). */
  resizableColumns: true;
  defaultColumnPrefs: TableColumnPref[] | undefined;
  onColumnPrefsChange: (prefs: TableColumnPref[]) => void;
  actions: TableCardAction[] | undefined;
};

export type UseServerTableOptions = {
  viewName: string;
  columns: TableFilterColumnConfig[];
  getSortByField: (columnId: string) => string | undefined;
  enabled?: boolean;
  enableUrlSync?: boolean;
  searchParams?: TableUrlSearchParams;
  defaultStoreType?: UseTableFilterOptionsOptions["defaultStoreType"];
  /**
   * Opt in only where the backend's list endpoint can actually serve
   * GROUPED_FETCH_LIMIT rows in one page — see the constant above. Off by
   * default so every other `useServerTable` caller keeps today's behavior
   * (Group By still renders, just only over the current page).
   */
  enableServerSideGrouping?: boolean;
  /**
   * Off for views where one saved page number would be wrong in another
   * context, such as a tabbed queue that shares one view preference across
   * tabs. Page size, search, sort and filters are still restored.
   */
  restoreSavedPage?: boolean;
  /**
   * Off where one view name serves several tabs whose columns differ, so prefs saved on one tab
   * would reorder the next (SERV-3423, the External Check Queue). Reset Columns goes with them:
   * with nothing being saved there is nothing to reset, and the action would otherwise still
   * appear for anyone whose browser kept prefs from before the view turned them off.
   */
  persistColumnPrefs?: boolean;
  /** Rows on the first request when neither the URL nor saved preferences set a limit. */
  defaultPageSize?: number;
};

export type UseServerTableResult = {
  navigate: ReturnType<typeof useNavigate>;
  queryOptions: ServerTableQueryOptions;
  isPrefsLoading: boolean;
  controlProps: ServerTableControlProps;
  /**
   * The active group column, re-exported so callers can tell whether the rows
   * on screen are grouped. Set on every caller, not just the ones that opt in:
   * the library Table notifies `onGroup` on all of them.
   */
  groupBy: TableSortState;
  /**
   * Put on the `<Table>` directly as `key={tableKey}`, never inside the spread: React rejects a
   * `key` that arrives through one. It changes only when Reset Columns is used, which is the
   * one case needing a remount, since the library reads `defaultColumnPrefs` on mount alone.
   */
  tableKey: string;
};

export function useServerTable({
  viewName,
  columns,
  getSortByField,
  enabled = true,
  enableUrlSync = true,
  searchParams = {},
  defaultStoreType = "workOrders",
  enableServerSideGrouping = false,
  restoreSavedPage = true,
  persistColumnPrefs = true,
  defaultPageSize,
}: UseServerTableOptions): UseServerTableResult {
  const navigate = useNavigate();

  const initialAppliedOptions = useMemo(
    () => parseFilterByToColumns(searchParams.filterBy, columns).appliedOptions,
    // Mount-only seed — captures the URL filterBy present on first render.
    [],
  );

  const filterShim = useTableFilterOptions(columns, {
    enabled,
    defaultStoreType,
    initialAppliedOptions,
  });

  const { toFilterEntries } = filterShim;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    setSearch,
    debouncedSearch,
    sorting,
    onSort,
    groupBy,
    onGroup,
    filters,
    onFilter,
    filtersKey,
    isPrefsLoading,
  } = useTablePersistence({
    viewName,
    columns,
    searchParams,
    navigate,
    enableUrlSync,
    toFilterEntries,
    seedAppliedOptions: filterShim.seedAppliedOptions,
    restoreSavedPage,
    defaultPageSize,
  });

  const isGroupedFetch = enableServerSideGrouping && !!groupBy;

  const queryOptions = useMemo<ServerTableQueryOptions>(
    () => ({
      page: isGroupedFetch ? 1 : page,
      pageSize: isGroupedFetch ? GROUPED_FETCH_LIMIT : pageSize,
      search: debouncedSearch || undefined,
      sortBy: sorting ? getSortByField(sorting.columnId) : undefined,
      sortOrder: sorting?.direction,
      filters: toFilterEntries(filters),
    }),
    [
      page,
      pageSize,
      isGroupedFetch,
      debouncedSearch,
      sorting,
      filtersKey,
      toFilterEntries,
      filters,
      getSortByField,
    ],
  );

  const effectiveRowCeiling = isGroupedFetch ? GROUPED_FETCH_LIMIT : pageSize;

  /**
   * The Table's footer Select binds `value: String(pageSize)` against this
   * list, and a Mantine Select whose value is absent from its data renders
   * blank. The app's default page size (100) and GROUPED_FETCH_LIMIT are both
   * outside the library's own default list, so fold the effective size in.
   */
  const pageSizeOptions = useMemo(
    () =>
      TABLE_PAGE_SIZE_OPTIONS.includes(effectiveRowCeiling)
        ? TABLE_PAGE_SIZE_OPTIONS
        : [...TABLE_PAGE_SIZE_OPTIONS, effectiveRowCeiling].sort((a, b) => a - b),
    [effectiveRowCeiling],
  );

  const { defaultColumnPrefs, onColumnPrefsChange, canReset, resetColumns, tableKey } =
    useTableColumnPrefs(viewName);

  const controlProps: ServerTableControlProps = {
    tableName: viewName,
    serverSide: true,
    // Mirrors queryOptions above -- the Table's own pager must agree with
    // how much data it was actually handed, or the footer range and page
    // buttons go out of sync with the grouped body (which renders every
    // fetched row, not just one page's worth).
    page: isGroupedFetch ? 1 : page,
    pageSize: isGroupedFetch ? GROUPED_FETCH_LIMIT : pageSize,
    pageSizeOptions,
    sorting,
    onSort,
    onGroup,
    filters,
    onFilter,
    onSearch: setSearch,
    filterOptions: filterShim.filterOptions,
    onFetchFilterOptions: filterShim.onFetchFilterOptions,
    onSearchFilterOptions: filterShim.onSearchFilterOptions,
    onLoadMoreFilterOptions: filterShim.onLoadMoreFilterOptions,
    // While a grouped fetch is active the query is pinned to page 1 at
    // GROUPED_FETCH_LIMIT, so accepting either control would write state the
    // user cannot see take effect -- and would then apply it the moment they
    // cleared the group column (jumping to a page they never reached, or
    // persisting a 1000-row page size into every later visit). The controls
    // stay rendered so the row count remains visible; the notice explains why
    // they do nothing.
    onPageChange: isGroupedFetch ? noop : setPage,
    onPageSizeChange: isGroupedFetch
      ? noop
      : (newPageSize: number) => {
          setPageSize(newPageSize);
          setPage(1);
        },
    cellClickIgnoreColumns: ["more"],
    // Column widths, order and visibility are remembered per person per browser, keyed by the
    // view name — the same scope the library already uses for sort order. A view that opts out
    // keeps the drag and loses the width on the next reload or tab switch, which is the lesser
    // harm there: its columns differ per tab, so remembering them under one name is what did
    // the damage.
    resizableColumns: true,
    defaultColumnPrefs: persistColumnPrefs ? defaultColumnPrefs : undefined,
    onColumnPrefsChange: persistColumnPrefs ? onColumnPrefsChange : noop,
    // No `useServerTable` screen passes its own `actions` today; one that starts to must merge
    // this in rather than replace it, or its list loses the only way back from a resize.
    actions: persistColumnPrefs && canReset ? [resetColumnsAction(resetColumns)] : undefined,
  };

  /*
   * `tableKey` is returned separately rather than put in `controlProps`, and every screen puts
   * it on its own `<Table key={tableKey} {...controlProps} />`. React refuses a `key` that
   * arrives through a spread ("React keys must be passed directly to JSX"), and it logged that
   * on every list in the app when it lived in `controlProps`.
   *
   * It only changes when Reset Columns is used, which is also the one thing that needs the
   * remount: the library reads `defaultColumnPrefs` once, on mount.
   */
  return { navigate, queryOptions, isPrefsLoading, controlProps, groupBy, tableKey };
}
